import React, { useMemo, useState, useCallback } from 'react';
import { COLORS, formatCurrency } from './InventoryUIKit';
import InventoryProgress from './InventoryProgress';
import PurchaseStatusBadge from './PurchaseStatusBadge';
import PurchaseActions from './PurchaseActions';
import { Package, Copy, Check, ChevronUp, ChevronDown, PackageX } from 'lucide-react';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'Invalid date';
  }
};

const formatQty = (n) => Number(n).toLocaleString('en-IN');

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      title="Copy invoice number"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 6px', borderRadius: '4px', border: '1px solid #e5e7eb',
        backgroundColor: copied ? '#f0fdf4' : '#ffffff', color: copied ? '#16a34a' : '#6b7280',
        fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => { if (!copied) e.currentTarget.style.borderColor = '#11a8a4'; }}
      onMouseLeave={(e) => { if (!copied) e.currentTarget.style.borderColor = '#e5e7eb'; }}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {text || '—'}
    </button>
  );
};

const SORT_KEYS = {
  purchase_date: 'purchase_date',
  invoice_number: 'invoice_number',
  supplier: 'supplier',
  purchase_quantity: 'purchase_quantity',
  quantity_remaining: 'quantity_remaining',
  unit_price: 'unit_price',
  total_purchase_value: 'total_purchase_value',
  remaining_value: 'remaining_value',
};

const PAGE_SIZES = [10, 25, 50, 100];

const SkeletonRows = ({ count = 5 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
        {Array.from({ length: 12 }).map((__, j) => (
          <td key={j} style={{ padding: '12px' }}>
            <div
              style={{
                height: j === 6 ? '24px' : '16px',
                width: j === 6 ? '100%' : `${50 + Math.random() * 40}%`,
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s infinite',
              }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const EmptyState = () => (
  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
    <PackageX size={56} color="#d1d5db" style={{ marginBottom: '16px' }} />
    <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '600', color: '#374151' }}>
      No Purchase Batches Found
    </h3>
    <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>
      Create your first purchase batch.
    </p>
  </div>
);

const PurchaseBatchTable = ({
  batches = [],
  loading = false,
  error = null,
  onRetry,
  onView,
  onEdit,
  page,
  setPage,
  totalPages,
  totalItems,
  pageSize,
  setPageSize,
}) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sortedBatches = useMemo(() => {
    if (!sortKey) return batches;
    const copy = [...batches];
    copy.sort((a, b) => {
      let av, bv;
      if (sortKey === 'purchase_date') {
        av = a.purchase_date || '';
        bv = b.purchase_date || '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortKey === 'invoice_number') {
        av = a.invoice_number || '';
        bv = b.invoice_number || '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortKey === 'supplier') {
        av = a.supplier || '';
        bv = b.supplier || '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      av = Number(a[sortKey]) || 0;
      bv = Number(b[sortKey]) || 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [batches, sortKey, sortDir]);

  const handleSort = useCallback(
    (key) => {
      if (!SORT_KEYS[key]) return;
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey]
  );

  const SortIcon = useCallback(
    ({ columnKey }) => {
      if (!SORT_KEYS[columnKey]) return null;
      if (sortKey !== columnKey) {
        return <ChevronUp size={12} color="#d1d5db" style={{ marginLeft: '2px', opacity: 0.5 }} />;
      }
      return sortDir === 'asc' ? (
        <ChevronUp size={12} color="#11a8a4" style={{ marginLeft: '2px' }} />
      ) : (
        <ChevronDown size={12} color="#11a8a4" style={{ marginLeft: '2px' }} />
      );
    },
    [sortKey, sortDir]
  );

  const handlePageSizeChange = useCallback(
    (e) => {
      setPageSize(Number(e.target.value));
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const from = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, totalItems);

  const thStyle = (align = 'left', sortable = false) => ({
    padding: '10px 12px',
    textAlign: align,
    color: '#6b7280',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
    borderBottom: '2px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    backgroundColor: '#f9fafb',
    zIndex: 1,
  });

  const tdStyle = (align = 'left', isEven = false) => ({
    padding: '10px 12px',
    textAlign: align,
    color: '#1f2937',
    verticalAlign: 'middle',
    backgroundColor: isEven ? '#fafbfc' : '#ffffff',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background-color 0.12s ease',
    fontSize: '13px',
  });

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '4px', height: '18px', backgroundColor: '#6366f1', borderRadius: '2px', display: 'inline-block' }} />
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>Purchase Batches</h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
              {loading ? 'Loading...' : `${totalItems} batch${totalItems === 1 ? '' : 'es'} found`}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Rows:</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{
              border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px',
              fontSize: '12px', color: '#374151', backgroundColor: '#ffffff', cursor: 'pointer',
              outline: 'none',
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div style={{ textAlign: 'center', padding: '24px', color: '#ef4444' }}>
          <p style={{ marginBottom: '8px', fontSize: '14px' }}>{error}</p>
          {onRetry && (
            <button onClick={onRetry} style={{ color: '#11a8a4', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              Retry
            </button>
          )}
        </div>
      )}

      {!error && !loading && batches.length === 0 && <EmptyState />}

      {!error && (loading || batches.length > 0) && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={thStyle('left', true)} onClick={() => handleSort('purchase_date')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>Purchase Date <SortIcon columnKey="purchase_date" /></span>
                  </th>
                  <th style={thStyle('left', true)} onClick={() => handleSort('invoice_number')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>Invoice <SortIcon columnKey="invoice_number" /></span>
                  </th>
                  <th style={thStyle('left', false)}>Item</th>
                  <th style={thStyle('left', true)} onClick={() => handleSort('supplier')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>Supplier <SortIcon columnKey="supplier" /></span>
                  </th>
                  <th style={thStyle('right', true)} onClick={() => handleSort('purchase_quantity')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>Purchased Qty <SortIcon columnKey="purchase_quantity" /></span>
                  </th>
                  <th style={thStyle('right', true)} onClick={() => handleSort('quantity_remaining')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>Remaining Qty <SortIcon columnKey="quantity_remaining" /></span>
                  </th>
                  <th style={thStyle('left', false)}>Utilization</th>
                  <th style={thStyle('right', true)} onClick={() => handleSort('unit_price')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>Unit Price <SortIcon columnKey="unit_price" /></span>
                  </th>
                  <th style={thStyle('right', true)} onClick={() => handleSort('total_purchase_value')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>Purchase Value <SortIcon columnKey="total_purchase_value" /></span>
                  </th>
                  <th style={thStyle('right', true)} onClick={() => handleSort('remaining_value')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>Remaining Value <SortIcon columnKey="remaining_value" /></span>
                  </th>
                  <th style={thStyle('center', false)}>Status</th>
                  <th style={thStyle('center', false)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows count={8} />
                ) : (
                  sortedBatches.map((batch, idx) => {
                    const isEven = idx % 2 === 0;
                    return (
                      <tr
                        key={batch.id ?? idx}
                        style={{
                          transition: 'all 0.12s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0fdfa';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isEven ? '#fafbfc' : '#ffffff';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <td style={tdStyle('left', isEven)}>
                          {formatDate(batch.purchase_date)}
                        </td>
                        <td style={tdStyle('left', isEven)}>
                          <CopyButton text={batch.invoice_number} />
                        </td>
                        <td style={tdStyle('left', isEven)}>
                          <div>
                            <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '13px' }}>
                              {batch.inventory_item_name || '—'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                              #{batch.inventory_item_code || '—'}
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle('left', isEven)}>
                          {batch.supplier || '—'}
                        </td>
                        <td style={tdStyle('right', isEven)}>
                          <span style={{ fontFamily: "'SF Mono', 'Consolas', monospace", fontSize: '12px', fontWeight: '500' }}>
                            {formatQty(batch.purchase_quantity)}
                          </span>
                        </td>
                        <td style={tdStyle('right', isEven)}>
                          <span style={{ fontFamily: "'SF Mono', 'Consolas', monospace", fontSize: '12px', fontWeight: '500' }}>
                            {formatQty(batch.quantity_remaining)}
                          </span>
                        </td>
                        <td style={{ ...tdStyle('left', isEven), minWidth: '150px' }}>
                          <InventoryProgress
                            purchaseQuantity={batch.purchase_quantity}
                            quantityRemaining={batch.quantity_remaining}
                          />
                        </td>
                        <td style={tdStyle('right', isEven)}>
                          {formatCurrency(batch.unit_price)}
                        </td>
                        <td style={tdStyle('right', isEven)}>
                          <span style={{ fontWeight: '500' }}>
                            {formatCurrency(batch.total_purchase_value)}
                          </span>
                        </td>
                        <td style={tdStyle('right', isEven)}>
                          {formatCurrency(batch.remaining_value)}
                        </td>
                        <td style={{ ...tdStyle('center', isEven), whiteSpace: 'nowrap' }}>
                          <PurchaseStatusBadge batch={batch} />
                        </td>
                        <td style={{ ...tdStyle('center', isEven), whiteSpace: 'nowrap' }}>
                          <PurchaseActions batch={batch} onView={onView} onEdit={onEdit} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalItems > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
              flexWrap: 'wrap', gap: '8px',
            }}>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Showing {from}–{to} of {totalItems}
              </span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  style={{
                    border: '1px solid #e5e7eb', backgroundColor: '#ffffff', borderRadius: '6px',
                    padding: '5px 12px', fontSize: '12px', cursor: page <= 1 ? 'default' : 'pointer',
                    opacity: page <= 1 ? 0.5 : 1, color: '#374151', fontWeight: '500',
                  }}
                >
                  Prev
                </button>
                <span style={{ fontSize: '12px', color: '#374151', padding: '0 8px', fontWeight: '500' }}>
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  style={{
                    border: '1px solid #e5e7eb', backgroundColor: '#ffffff', borderRadius: '6px',
                    padding: '5px 12px', fontSize: '12px', cursor: page >= totalPages ? 'default' : 'pointer',
                    opacity: page >= totalPages ? 0.5 : 1, color: '#374151', fontWeight: '500',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(PurchaseBatchTable);
